import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import {
  COUNTRIES,
  countryMatchesSearch,
  getCountryDisplayName,
  getCountryLabel,
  type Country,
} from '../../data/countries';

export interface CountryMultiSelectProps {
  /**
   * Optional id applied to the search input so an external
   * `<label htmlFor>` can associate the control with its visible label.
   */
  id?: string;
  /**
   * Selected countries as ISO alpha2 codes (the canonical shape shared
   * with the homepage). Legacy full country names are tolerated so
   * existing records neither render as blanks nor get re-added.
   */
  values: string[];
  /** Called with the next selection whenever a country is added or removed. */
  onChange: (values: string[]) => void;
  placeholder?: string;
  noResultsText?: string;
  /** Builds the accessible label for a chip's remove button. */
  removeAriaLabel?: (label: string) => string;
  className?: string;
  disabled?: boolean;
}

// Legacy country labels the backend already canonicalizes (#302): stored
// rows may still hold "UK" or "Czech Republic" from before the migration.
// They resolve to the same identity as their canonical entry so selected
// chips and the option list agree with the backend. Keep in sync with the
// backend catalog; do not grow this into a second country catalog.
const LEGACY_COUNTRY_ALIASES: Record<string, string> = {
  uk: 'GB',
  'czech republic': 'CZ',
};

// Resolve a stored value to its country. Values are expected to be ISO
// alpha2 codes, but legacy records may still hold full country names or a
// known alias, so all three are matched (case-insensitively). A value that
// resolves to nothing is rendered as-is rather than dropped, so no data is
// lost.
const findCountryByValue = (value: string): Country | undefined => {
  const needle = value.trim().toLowerCase();
  if (!needle) {
    return undefined;
  }

  const aliasCode = LEGACY_COUNTRY_ALIASES[needle];
  if (aliasCode) {
    return COUNTRIES.find((country) => country.alpha2 === aliasCode);
  }

  return COUNTRIES.find(
    (country) =>
      country.alpha2.toLowerCase() === needle ||
      country.name.toLowerCase() === needle,
  );
};

export const CountryMultiSelect: React.FC<CountryMultiSelectProps> = ({
  id,
  values,
  onChange,
  placeholder = '',
  noResultsText = '',
  removeAriaLabel,
  className = '',
  disabled = false,
}) => {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const listboxId = `${id ?? 'country-multiselect'}-listbox`;

  // Selected values are canonicalized to the same identity the option list
  // uses, so an alias such as "UK" marks "United Kingdom" as already
  // selected (and it cannot be re-added), while a truly unknown value keeps
  // its raw form. Already-selected countries are removed from the option
  // list below, so the same country can never be added twice.
  const selectedKeys = new Set(
    values.map((value) => findCountryByValue(value)?.alpha2 ?? value),
  );

  const isSelected = (country: Country) => selectedKeys.has(country.alpha2);

  const availableCountries = COUNTRIES.filter((country) => !isSelected(country));

  // Mirror the homepage autocomplete's matching (via the shared helper) so
  // the two country pickers feel identical and search follows the active
  // language: 1-2 characters match name OR alpha2 by prefix, 3+ match name.
  const filterCountries = (search: string): Country[] => {
    if (!search.trim()) {
      return availableCountries;
    }

    return availableCountries.filter((country) =>
      countryMatchesSearch(country, search, language),
    );
  };

  const filteredCountries = isOpen ? filterCountries(searchValue) : [];

  const handleSelectCountry = (country: Country) => {
    if (isSelected(country)) {
      return;
    }
    onChange([...values, country.alpha2]);
    setSearchValue('');
    setHighlightedIndex(-1);
    // Keep focus (and the dropdown) so the admin can pick several
    // countries in a row without reopening the field each time.
    inputRef.current?.focus();
  };

  const handleRemove = (value: string) => {
    onChange(values.filter((item) => item !== value));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleInputBlur = () => {
    // Delay closing so an option's mousedown can land before blur tears
    // the list down. Only close when focus actually left the component.
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    }, 150);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredCountries.length - 1 ? prev + 1 : prev,
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredCountries[highlightedIndex]) {
          handleSelectCountry(filteredCountries[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      case 'Backspace':
        // With an empty query, Backspace removes the most recently added
        // chip — a familiar multi-select shortcut.
        if (searchValue === '' && values.length > 0) {
          e.preventDefault();
          handleRemove(values[values.length - 1]);
        }
        break;
      case 'Tab':
        setIsOpen(false);
        break;
    }
  };

  // Scroll the highlighted option into view.
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightedIndex] as HTMLElement;
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  // Close the dropdown when clicking outside the component.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <div
        className={`flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-slate-300 px-2 py-1.5 focus-within:ring-2 focus-within:ring-slate-400 ${
          disabled ? 'bg-slate-100 opacity-60' : 'bg-white'
        } ${className}`}
      >
        {values.map((value) => {
          const country = findCountryByValue(value);
          const label = country ? getCountryDisplayName(country, language) : value;

          return (
            <span
              key={value}
              className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
            >
              {label}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(value)}
                  aria-label={removeAriaLabel ? removeAriaLabel(label) : label}
                  className="rounded text-slate-500 hover:text-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-400"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          );
        })}

        <input
          id={id}
          ref={inputRef}
          type="text"
          value={searchValue}
          disabled={disabled}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={values.length === 0 ? placeholder : ''}
          className="h-7 min-w-[8rem] flex-1 border-0 bg-transparent text-sm focus:outline-none"
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          aria-autocomplete="list"
        />
      </div>

      {isOpen && filteredCountries.length > 0 && (
        <ul
          ref={listRef}
          id={listboxId}
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg"
          role="listbox"
        >
          {filteredCountries.map((country, index) => (
            <li
              key={country.alpha2}
              role="option"
              aria-selected={index === highlightedIndex}
              onMouseDown={(event) => {
                event.preventDefault();
                handleSelectCountry(country);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`px-3 py-2 text-sm cursor-pointer ${
                index === highlightedIndex
                  ? 'bg-slate-100 text-slate-900'
                  : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              {getCountryLabel(country, language)}
            </li>
          ))}
        </ul>
      )}

      {isOpen && filteredCountries.length === 0 && searchValue.trim() && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-500 shadow-lg">
          {noResultsText}
        </div>
      )}
    </div>
  );
};
